/**
 * File list — renderiza linhas com 4 estados (queued/processing/done/error).
 *
 * SEMPRE usa textContent pra filename (NUNCA innerHTML) — defesa contra XSS.
 * A11y: role="list" no container, role="listitem" em cada linha.
 */
import { t } from '@/i18n/index.js';
import { formatBytes, reductionPct } from '@/utils/size.js';
import { sanitizeFilename } from '@/utils/sanitize.js';
import { uniqueName } from '@/utils/files.js';

/**
 * @typedef {Object} FileEntry
 * @property {string} id            — id interno (gerado)
 * @property {string} name          — nome original
 * @property {number} size          — bytes
 * @property {string} type          — 'image/png' etc
 * @property {ArrayBuffer} buffer   — bytes do arquivo
 * @property {string} status        — 'queued' | 'processing' | 'done' | 'error'
 * @property {number} [compressedSize]
 * @property {string} [error]
 * @property {string} [displayName] — nome único no zip
 */

/**
 * Cria o gerenciador da file list.
 * @param {HTMLUListElement} el
 * @param {{
 *   onRemove: (id: string) => void,
 *   onAllDone?: () => void
 * }} cb
 */
export function mountFileList(el, cb) {
  if (!el) return;
  if (el.dataset.mounted === 'file-list') return;
  el.dataset.mounted = 'file-list';
  el.setAttribute('role', 'list');
  el.setAttribute('aria-live', 'polite');

  const usedNames = new Set();

  /** @type {Map<string, FileEntry>} */
  const entries = new Map();

  function render(entry) {
    let row = el.querySelector(`[data-id="${entry.id}"]`);
    if (!row) {
      row = createRow(entry);
      el.appendChild(row);
    }
    updateRow(row, entry);
  }

  function remove(id) {
    const row = el.querySelector(`[data-id="${id}"]`);
    if (row) {
      row.style.opacity = '0';
      row.style.transform = 'translateX(8px)';
      setTimeout(() => row.remove(), 200);
    }
    const e = entries.get(id);
    if (e) {
      usedNames.delete(e.displayName);
      entries.delete(id);
    }
    cb.onRemove(id);
    if (entries.size === 0 && cb.onAllDone) cb.onAllDone();
  }

  return {
    /**
     * Adiciona arquivos à lista. Cada file deve ter {file, type, buffer} (vindo do dropzone).
     * Retorna os entries criados.
     */
    add(files) {
      const created = [];
      for (const f of files) {
        const id = `f-${++counter}`;
        const displayName = uniqueName(sanitizeFilename(f.file.name) || 'image', usedNames);
        usedNames.add(displayName);
        const entry = {
          id,
          name: f.file.name,
          displayName,
          size: f.file.size,
          type: f.type,
          buffer: f.buffer,
          status: 'queued',
        };
        entries.set(id, entry);
        render(entry);
        created.push(entry);
      }
      return created;
    },

    setStatus(id, status, extra = {}) {
      const entry = entries.get(id);
      if (!entry) return;
      entry.status = status;
      Object.assign(entry, extra);
      render(entry);
    },

    getAll() {
      return [...entries.values()];
    },

    getDone() {
      return [...entries.values()].filter((e) => e.status === 'done');
    },

    /**
     * Stats agregados pra indicador de progresso.
     * @returns {{total: number, queued: number, processing: number, done: number, error: number}}
     */
    getStats() {
      const stats = { total: 0, queued: 0, processing: 0, done: 0, error: 0 };
      for (const e of entries.values()) {
        stats.total++;
        stats[e.status] = (stats[e.status] || 0) + 1;
      }
      return stats;
    },

    get(id) {
      return entries.get(id);
    },

    clear() {
      el.innerHTML = '';
      entries.clear();
      usedNames.clear();
    },

    remove,
  };
}

let counter = 0;

function createRow(entry) {
  const row = document.createElement('li');
  row.className = 'file-row';
  row.dataset.id = entry.id;
  row.dataset.status = entry.status;
  row.setAttribute('role', 'listitem');

  // status icon
  const statusEl = document.createElement('div');
  statusEl.className = 'file-status';
  statusEl.dataset.role = 'status-icon';

  // name
  const nameEl = document.createElement('div');
  nameEl.className = 'file-name';
  nameEl.dataset.role = 'name';

  // size
  const sizeEl = document.createElement('div');
  sizeEl.className = 'file-size';
  sizeEl.dataset.role = 'size';

  // reduction
  const reductionEl = document.createElement('div');
  reductionEl.className = 'file-reduction';
  reductionEl.dataset.role = 'reduction';

  // remove button
  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'file-remove';
  removeBtn.setAttribute('aria-label', t('fileList.removeAria', { filename: entry.name }));
  removeBtn.dataset.role = 'remove';
  removeBtn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  `;

  row.append(statusEl, nameEl, sizeEl, reductionEl, removeBtn);
  return row;
}

function updateRow(row, entry) {
  row.dataset.status = entry.status;

  const statusEl = row.querySelector('[data-role="status-icon"]');
  const nameEl = row.querySelector('[data-role="name"]');
  const sizeEl = row.querySelector('[data-role="size"]');
  const reductionEl = row.querySelector('[data-role="reduction"]');
  const removeBtn = row.querySelector('[data-role="remove"]');

  // status icon
  if (entry.status === 'queued') {
    statusEl.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" /></svg>';
  } else if (entry.status === 'processing') {
    statusEl.innerHTML = '<div class="spinner" aria-label="Processing"></div>';
  } else if (entry.status === 'done') {
    statusEl.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>';
  } else if (entry.status === 'error') {
    statusEl.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>';
  }

  // name — SEMPRE textContent (XSS defense)
  nameEl.textContent = entry.displayName || entry.name;

  // size
  if (entry.status === 'done' && entry.compressedSize !== null && entry.compressedSize !== undefined) {
    sizeEl.textContent = `${formatBytes(entry.size)} → ${formatBytes(entry.compressedSize)}`;
  } else {
    sizeEl.textContent = formatBytes(entry.size);
  }

  // reduction
  if (entry.status === 'done' && entry.compressedSize !== null && entry.compressedSize !== undefined) {
    const pct = reductionPct(entry.size, entry.compressedSize);
    if (pct > 0) {
      reductionEl.textContent = `−${pct}%`;
    } else if (pct === 0) {
      reductionEl.textContent = t('fileList.noReduction');
    } else {
      reductionEl.textContent = `${t('fileList.larger')} +${Math.abs(pct)}%`;
    }
  } else if (entry.status === 'processing') {
    reductionEl.textContent = t('fileList.processing');
  } else if (entry.status === 'error') {
    reductionEl.textContent = entry.error || t('fileList.error');
  } else {
    reductionEl.textContent = '';
  }

  // remove button listener
  removeBtn.onclick = (e) => {
    e.stopPropagation();
    if (row._list) row._list.remove(entry.id);
  };
  row._list = row._list || getListHandle(row);
}

let _listHandle = null;
function getListHandle(_row) {
  // pega o handle do escopo do mount — hack leve pra não ter que armazenar
  // uma ref em cada row
  return _listHandle;
}

export function _setListHandle(h) {
  _listHandle = h;
}
